import { useEffect, useState, type CSSProperties } from "react";
import { C, FONT } from "../theme";
import type { BalanceDay } from "../types";

/**
 * Bespoke water-balance chart (no chart lib).
 * Stacked vertical bars per day — rainfall at the bottom, irrigation applied
 * stacked above — with an ET₀ demand line drawn over the top. Bars grow
 * scaleY 0→1 on mount; the ET line draws via strokeDashoffset 1→0.
 */
export function BalanceChart({ data }: { data: BalanceDay[] }) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setMounted(true));
    return () => cancelAnimationFrame(id);
  }, []);

  // ── Internal coordinate space (SVG scales to container via viewBox) ──
  const VW = 560;
  const VH = 218;
  const padL = 30; // room for Y-axis labels
  const padR = 8;
  const padT = 10;
  const padB = 22; // room for X-axis labels
  const plotW = VW - padL - padR;
  const plotH = VH - padT - padB;
  const baseY = padT + plotH; // bottom of plot (y for value 0)

  // Single scale max from the larger of (rain+applied) or ET per day, + headroom.
  const rawMax = data.reduce(
    (m, d) => Math.max(m, d.rain + d.applied, d.et),
    0,
  );
  const max = rawMax > 0 ? rawMax * 1.15 : 1;

  const BAR_W = 17;
  const GRID_LINES = 4; // horizontal grid divisions
  const GRID = C.track;
  const RAIN = C.rain;
  // Axis/tick numerals must clear WCAG AA on the paper surface, so use a
  // mid-emphasis text token rather than the old light greys (which failed 4.5:1).
  const Y_LABEL = C.textMid;
  const X_LABEL = C.textMid;

  const yFor = (v: number) => baseY - (v / max) * plotH;
  const colCenter = (i: number) =>
    padL + (plotW / data.length) * (i + 0.5);

  // Grid + Y-axis tick values.
  const ticks = Array.from({ length: GRID_LINES + 1 }, (_, i) => {
    const v = (max / GRID_LINES) * i;
    return { v, y: yFor(v) };
  });

  // ET₀ polyline path over the bars.
  const etPath = data
    .map((d, i) => `${i === 0 ? "M" : "L"}${colCenter(i)} ${yFor(d.et)}`)
    .join(" ");

  return (
    <div style={{ width: "100%", height: VH }}>
      <svg
        viewBox={`0 0 ${VW} ${VH}`}
        width="100%"
        height={VH}
        preserveAspectRatio="none"
        style={{ display: "block", overflow: "visible" }}
      >
        {/* Horizontal grid lines + Y-axis labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line
              x1={padL}
              x2={VW - padR}
              y1={t.y}
              y2={t.y}
              stroke={GRID}
              strokeWidth={1}
            />
            <text
              x={padL - 6}
              y={t.y}
              textAnchor="end"
              dominantBaseline="middle"
              fontFamily={FONT.mono}
              fontSize={9.5}
              fill={Y_LABEL}
            >
              {t.v.toFixed(1)}
            </text>
          </g>
        ))}

        {/* Stacked bars: rain at bottom, applied above */}
        {data.map((d, i) => {
          const cx = colCenter(i);
          const x = cx - BAR_W / 2;
          const rainH = (d.rain / max) * plotH;
          const appliedH = (d.applied / max) * plotH;
          const rainY = baseY - rainH;
          const appliedY = rainY - appliedH;
          // Each bar grows from its own bottom edge (transform-origin bottom).
          const grow = (originY: number): CSSProperties => ({
            transform: mounted ? "scaleY(1)" : "scaleY(0)",
            transformOrigin: `0px ${originY}px`,
            transformBox: "view-box",
            transition: `transform 1s cubic-bezier(.2,.8,.2,1) ${i * 0.05}s`,
          });
          return (
            <g key={d.d}>
              {rainH > 0 && (
                <rect
                  x={x}
                  y={rainY}
                  width={BAR_W}
                  height={rainH}
                  rx={2.5}
                  fill={RAIN}
                  style={grow(baseY)}
                />
              )}
              {appliedH > 0 && (
                <path
                  d={`M${x} ${appliedY + appliedH}
                      L${x} ${appliedY + 2.5}
                      Q${x} ${appliedY} ${x + 2.5} ${appliedY}
                      L${x + BAR_W - 2.5} ${appliedY}
                      Q${x + BAR_W} ${appliedY} ${x + BAR_W} ${appliedY + 2.5}
                      L${x + BAR_W} ${appliedY + appliedH} Z`}
                  fill={C.sea}
                  style={grow(rainY)}
                />
              )}
              <text
                x={cx}
                y={VH - 6}
                textAnchor="middle"
                fontFamily={FONT.sans}
                fontSize={10.5}
                fill={X_LABEL}
              >
                {d.d}
              </text>
            </g>
          );
        })}

        {/* ET₀ demand line, drawn on mount */}
        <path
          d={etPath}
          fill="none"
          stroke={C.beacon}
          strokeWidth={2.2}
          strokeLinejoin="round"
          strokeLinecap="round"
          pathLength={1}
          style={{
            strokeDasharray: 1,
            strokeDashoffset: mounted ? 0 : 1,
            transition: "stroke-dashoffset 1.4s cubic-bezier(.2,.8,.2,1) .35s",
          }}
        />
        {data.map((d, i) => (
          <circle
            key={`et-${d.d}`}
            cx={colCenter(i)}
            cy={yFor(d.et)}
            r={2.6}
            fill={C.paper}
            stroke={C.beacon}
            strokeWidth={1.6}
            style={{
              opacity: mounted ? 1 : 0,
              transition: `opacity .3s ease ${0.35 + 1.4 + i * 0.04}s`,
            }}
          />
        ))}
      </svg>
    </div>
  );
}
